import React from 'react';

import { SwipeableContext } from '../contexts/SwipeableContext';

interface ContentProps {
  // You can set a specific component containg all the slides you want to render.
  // In that component probably you want to wrap it with a library to manage the swiping events
  // and you will be able to set your own type of content.
  slides: React.ReactNode;
  // In order to avoid this component to calculate the number of slides the `slides` props is setting
  // please provide the total number of slides you are going to manipulate
  total: number;
}

// Calculates next index based on the total
// it allows to go to previous and next index but applying
// a mod operator in order to check boundaries
// so the user is not able to select indexes outside boundaries
const calculateModIndex = (index: number, total: number) => {
  if (total === 0) {
    return 0;
  }

  if (index < 0) {
    return total - 1;
  }
  if (index + 1 > total) {
    return 0;
  }

  return index % total;
};

interface Props {
  content: ContentProps;
}

interface DefaultProps {
  initialSlideIndex?: number;
  initialContentIndex?: number;
}

interface State {
  slideIndex: number;
}

const getInitialState = ({
  initialSlideIndex,
  content,
}: DefaultProps & Props): State => ({
  slideIndex: calculateModIndex(initialSlideIndex || 0, content.total),
});

class SwipeableProvider extends React.Component<DefaultProps & Props> {
  static defaultProps: DefaultProps = {
    initialSlideIndex: 0,
    initialContentIndex: 0,
  };

  state = getInitialState(this.props);

  setSlide = (index: number) => {
    const {
      content: { total },
    } = this.props;

    this.setState({
      slideIndex: calculateModIndex(index, total),
    });
  };

  handleNext = () => {
    const { slideIndex } = this.state;

    this.setSlide(slideIndex + 1);
  };

  handlePrevious = () => {
    const { slideIndex } = this.state;

    this.setSlide(slideIndex - 1);
  };

  handleChangeIndex = (index: number) => {
    this.setSlide(index);
  };

  render() {
    const { slideIndex } = this.state;

    return (
      <SwipeableContext.Provider
        value={{
          slides: this.props.content.slides,
          slideIndex,
          total: this.props.content.total,
          handleNext: this.handleNext,
          handlePrevious: this.handlePrevious,
          handleChangeSlideIndex: this.handleChangeIndex,
        }}
      >
        {this.props.children}
      </SwipeableContext.Provider>
    );
  }
}

export { SwipeableProvider };
